// Product rows and media loading are independent. Keep the Shopify page cursor,
// and buffer real product nodes until the next complete responsive row is needed.
window.VelveleGridPagination = window.VelveleGridPagination || (function () {
  const instances = new WeakMap();
  const active = new Set();

  function get(button) {
    if (!button?.matches('.js-infinite-scroll')) return null;
    const scope = button.closest('.collection');
    const grid = scope?.querySelector('#product-grid.load-more-grid');
    if (!grid) return null;
    if (instances.has(grid)) return instances.get(grid);

    const nextInput = scope.querySelector('[data-next-url]');
    const pageInput = scope.querySelector('[data-current-page]');
    const totalInput = scope.querySelector('[data-total-pages]');
    if (!nextInput || !pageInput || !totalInput) return null;
    let next = nextInput.value;
    let page = Number(pageInput.value);
    let more = !!next && page < Number(totalInput.value);
    let pending = null;
    let failed = false;
    let disposed = false;
    let frame = 0;
    let observer;
    const queue = [];
    const spinnerContent = button.innerHTML;
    const key = item => item.querySelector('a[href*="/products/"]')?.getAttribute('href')?.split('?')[0];
    const seen = new Set(Array.from(grid.children, key).filter(Boolean));
    const fetched = new Set();
    const count = () => grid.children.length;
    const columns = () => {
      const card = grid.firstElementChild;
      if (!card) return 1;
      const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
      return Math.max(1, Math.round((grid.getBoundingClientRect().width + gap) / (card.getBoundingClientRect().width + gap)));
    };
    const margin = () => Math.min(800, Math.max(320, Math.round(innerHeight * 0.75)));
    const near = () => button.getBoundingClientRect().top <= innerHeight + margin();

    function dispose() {
      disposed = true;
      observer?.disconnect();
      cancelAnimationFrame(frame);
      active.delete(api);
    }

    function append(amount) {
      if (!amount || disposed || !grid.isConnected) return;
      const fragment = document.createDocumentFragment();
      queue.splice(0, amount).forEach(item => fragment.appendChild(item));
      grid.appendChild(fragment);
      if (typeof colorSwatches === 'function') colorSwatches();
      grid.dispatchEvent(new CustomEvent('velvele:grid:updated', { bubbles: true }));
    }

    function finish() {
      if (!more && !queue.length) {
        button.remove();
        dispose();
      }
    }

    async function fetchPage() {
      if (!more || disposed || !grid.isConnected) return;
      const url = new URL(next, location.href);
      if (grid.dataset.id) url.searchParams.set('section_id', grid.dataset.id);
      if (fetched.has(url.href)) throw new Error('Repeated pagination URL');
      const response = await fetch(url.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Product page request failed');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (disposed || !grid.isConnected) return;
      const incoming = doc.querySelector('#product-grid');
      if (!incoming || incoming.dataset.id !== grid.dataset.id) throw new Error('Product grid missing from response');
      const source = incoming.closest('.collection');
      const responsePage = Number(source?.querySelector('[data-current-page]')?.value || page + 1);
      const responseNext = source?.querySelector('[data-next-url]')?.value || '';
      if (responsePage <= page) throw new Error('Product pagination did not advance');
      fetched.add(url.href);
      Array.from(incoming.children).filter(item => item.matches(".collection-product-card")).forEach(item => {
        const id = key(item);
        if (id && seen.has(id)) return;
        if (id) seen.add(id);
        queue.push(item);
      });
      page = responsePage;
      next = responseNext;
      more = !!next && page < Number(totalInput.value);
      pageInput.value = String(page);
      nextInput.value = next;
      pageInput.dataset.currentPage = String(page);
      nextInput.dataset.nextUrl = next;
    }

    async function pump(reason) {
      if (disposed || !grid.isConnected || failed) return;
      if (pending) return pending;
      pending = (async () => {
        button.setAttribute('aria-busy', 'true');
        try {
          // Complete only the current row first, even if a server page excluded
          // collection-tagged products. Do not expose an incomplete new batch.
          let missing = (columns() - count() % columns()) % columns();
          if (!count() && more) missing = columns();
          while (queue.length < missing && more && !disposed && grid.isConnected) {
            await fetchPage();
            missing = (columns() - count() % columns()) % columns();
          }
          if (missing) append(Math.min(missing, queue.length));
          if (reason === 'near' && near() && !disposed) {
            while (queue.length < columns() && more && !disposed && grid.isConnected) await fetchPage();
            const amount = more ? Math.floor(queue.length / columns()) * columns() : queue.length;
            append(amount);
          }
          if (!more) append(queue.length); // True catalog end may have a partial row.
          finish();
        } catch (error) {
          failed = true;
          observer?.disconnect();
          button.classList.remove('loading');
          button.replaceChildren();
          const retry = document.createElement('button');
          retry.type = 'button';
          retry.className = 'button button--secondary';
          retry.textContent = 'Retry loading products';
          retry.addEventListener('click', () => { failed = false; button.innerHTML = spinnerContent; button.classList.add('loading'); pump('near'); });
          button.appendChild(retry);
          console.error('Unable to load more products', error);
        } finally {
          button.removeAttribute('aria-busy');
        }
      })();
      await pending;
      pending = null;
      if (!disposed && !failed) schedule();
    }

    function schedule() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (disposed || failed || !grid.isConnected) return;
        observer?.disconnect();
        observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) pump('near');
        }, { rootMargin: '0px 0px ' + margin() + 'px 0px' });
        observer.observe(button);
      });
    }

    const api = { grid, pump, dispose, resize() { if (!grid.isConnected) return dispose(); pump('resize'); } };
    instances.set(grid, api);
    active.add(api);
    pump('initial');
    return api;
  }

  window.addEventListener('resize', () => active.forEach(instance => instance.resize()), { passive: true });
  document.addEventListener('shopify:section:unload', event => active.forEach(instance => { if (event.target.contains(instance.grid)) instance.dispose(); }));
  return { get, prune() { active.forEach(instance => { if (!instance.grid.isConnected) instance.dispose(); }); } };
})();

function loadItems (button) {
  const rowPager = window.VelveleGridPagination.get(button);
  if (rowPager) return rowPager.pump("near");
  const totalPages = parseInt(
    document.querySelector("[data-total-pages]").value,
    10
  );
  let currentPage = parseInt(
    document.querySelector("[data-current-page]").value,
    10
  );
  let currentPageScroll = currentPage + 1;

  currentPage = currentPage + 1;
  const nextUrl = document
    .querySelector("[data-next-url]")
    .value.replace(/page=[0-9]+/, "page=" + currentPage);
  const nextUrlScroll = document
    .querySelector("[data-next-url]")
    .value.replace(/page=[0-9]+/, "page=" + currentPageScroll);

  document.querySelector("[data-current-page]").value = currentPage;

  button.setAttribute("disabled", "");
  button.classList.add("loading");

  fetch(nextUrl)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.text();
    })
    .then((responseHTML) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(responseHTML, "text/html");
      const newItems = doc.querySelector(".load-more-grid").innerHTML;

      document
        .querySelector(".load-more-grid")
        .insertAdjacentHTML("beforeend", newItems);
      colorSwatches();
    })
    .catch((error) => {
      console.error("Error loading items:", error);
      button.removeAttribute("disabled");
      button.classList.remove("loading");
    })
    .finally(() => {
      if (currentPage <= totalPages) {
        const scrollData = document.querySelector(".infinite-scroll__data");
        if (scrollData && currentPage !== totalPages) {
          const nextUrlInput = scrollData.querySelector("input[data-next-url]");
          const currentPageInput = scrollData.querySelector(
            "input[data-current-page]"
          );

          if (nextUrlInput) {
            nextUrlInput.dataset.nextUrl = nextUrlScroll;
            nextUrlInput.value = nextUrlScroll;
          }

          if (currentPageInput) {
            currentPageInput.dataset.currentPage = currentPage;
            currentPageInput.value = currentPage;
          }

          checkVisibility();
        }

        button.removeAttribute("disabled");
        button.classList.remove("loading");

        if (currentPage === totalPages) {
          button.remove();
        }
      }
    });
};

function checkVisibility() {
  const spinnerList = document.querySelectorAll(".js-infinite-scroll");
  spinnerList.forEach((spinner) => {
    if (window.VelveleGridPagination.get(spinner)) return;
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          loadItems(spinner);
          sectionObserver.unobserve(entry.target);
        }
      });
    });

    sectionObserver.observe(spinner);
  });
};

function loadMore() {
  window.VelveleGridPagination.prune();
  document.querySelectorAll(".js-load-more").forEach((button) => {
    button.onclick = () => {
      loadItems(button);
    };
  });

  checkVisibility();
}

(function () {
  loadMore();
})();
